import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, Loader2, Phone, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import MessageBubble from "./assistant/MessageBubble";
import useAssistantStream from "../hooks/useAssistantStream";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IS_DEV = process.env.NODE_ENV !== "production";

const DEFAULT_WELCOME =
  "Bonjour 👋 Je suis l'assistant du CFMPE. Comment puis-je vous aider ?";

export default function Assistant() {
  const [welcome, setWelcome] = useState(DEFAULT_WELCOME);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const { messages, isLoading, sendMessage, reset } = useAssistantStream();

  // Fetch assistant config once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/assistant/config`);
        const data = await res.json();
        if (cancelled) return;
        if (data.welcome) setWelcome(data.welcome);
        if (Array.isArray(data.quick_questions)) setQuickQuestions(data.quick_questions);
      } catch (err) {
        if (IS_DEV) {
          console.warn("[Assistant] Failed to load config, using defaults", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
    setInput("");
  };

  const showQuickQuestions = messages.length === 0 && quickQuestions.length > 0;

  return (
    <section id="assistant" className="py-20 lg:py-28">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">
            Assistant IA CFMPE
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-[#0e3a3f] leading-[1.05] mt-3 title-underline-center">
            Posez vos questions,<br />réponse instantanée.
          </h2>
          <p className="text-[#0e3a3f]/70 mt-6 max-w-2xl mx-auto">
            Notre assistant vous renseigne 24/7 sur la formation, les tarifs, les financements et les débouchés.
          </p>
        </div>

        <div className="bg-white border border-[#e6ded0] rounded-3xl overflow-hidden shadow-xl shadow-[#0e3a3f]/5">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#0e3a3f] to-[#1a5459] text-white px-6 py-5 flex items-center gap-4">
            <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-[#2c7a80] to-[#4ba9b0] flex items-center justify-center shadow-lg">
              <Bot className="w-5 h-5 text-white" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#4ade80] border-2 border-[#0e3a3f]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white">Assistant CFMPE</div>
              <div className="text-xs text-white/60 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4ade80]" />
                En ligne · IA propulsée par le CFMPE
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={reset}
                title="Réinitialiser la conversation"
                className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="assistant-reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="h-[440px] overflow-y-auto px-4 md:px-6 py-6 space-y-4 bg-[#fbfaf7]"
          >
            {/* Welcome bubble */}
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#2c7a80] to-[#4ba9b0] flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="max-w-[80%] bg-white border border-[#e6ded0] rounded-2xl rounded-tl-sm px-4 py-3">
                <p className="text-sm text-[#0e3a3f] leading-relaxed whitespace-pre-wrap">
                  {welcome}
                </p>
              </div>
            </div>

            {showQuickQuestions && (
              <div className="pl-12 flex flex-wrap gap-2 pt-1">
                {quickQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    disabled={isLoading}
                    className="text-xs font-medium text-[#2c7a80] bg-white border border-[#2c7a80]/30 hover:bg-[#2c7a80] hover:text-white rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                    {q}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => (
              <MessageBubble key={m.id || `msg-${i}`} message={m} />
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={onSubmit}
            className="border-t border-[#e6ded0] p-4 bg-white flex items-center gap-3"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écrivez votre question…"
              disabled={isLoading}
              className="flex-1 h-11 border-[#e6ded0] focus-visible:ring-[#2c7a80]"
              data-testid="assistant-input"
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="bg-[#2c7a80] hover:bg-[#245e63] text-white rounded-full h-11 px-5 btn-primary"
              data-testid="assistant-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Envoyer</span>
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-xs text-[#0e3a3f]/50 text-center mt-4">
          L&apos;assistant peut faire des erreurs. Pour toute demande officielle, contactez-nous au{" "}
          <a href="tel:0493620029" className="font-semibold text-[#2c7a80] hover:underline">
            <Phone className="w-3 h-3 inline-block mr-0.5 -mt-0.5" />
            04.93.62.00.29
          </a>
          .
        </p>
      </div>
    </section>
  );
}
